/**
 * useWorkflowGuardCheck - Consulta antecipada da trava do Workflow
 *
 * Pergunta ao banco, ANTES do clique, se uma ação protegida está liberada para
 * o registro aberto. Reaproveita integralmente a RPC já existente
 * `workflow_guard_check` (mesma política usada pelo gatilho), sem criar RPC nova.
 *
 * Importante: esta consulta é apenas antecipação de feedback na interface.
 * A decisão real continua no banco (gatilho `workflow_tg_guard`); se algo passar
 * por aqui, o `WorkflowGuardNoticeProvider` segue como rede de segurança.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-03 v1.0.0 - Criação: bloqueio explicado antes do clique.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/** Resultado normalizado da política do processo */
export interface WorkflowGuardDecision {
  /** Política devolvida pelo banco */
  resultado: 'ALLOW' | 'WARN' | 'DENY';
  /** Mensagem configurada no processo (quando houver) */
  mensagem: string | null;
  /** Atalho: a ação está bloqueada */
  bloqueado: boolean;
  /** Atalho: a ação apenas avisa o usuário */
  avisa: boolean;
  /** Enquanto carrega, nada é bloqueado na interface */
  carregando: boolean;
}

const PADRAO_LIBERADO: Omit<WorkflowGuardDecision, 'carregando'> = {
  resultado: 'ALLOW',
  mensagem: null,
  bloqueado: false,
  avisa: false,
};

interface Params {
  /** Código da ação no catálogo (ex.: 'crm.proposal.create') */
  actionCode: string;
  /** Tipo da entidade-raiz (ex.: 'crm_deal') */
  rootEntityType?: string | null;
  /** Id da entidade-raiz */
  rootEntityId?: string | null;
  /** Permite desligar a consulta (ex.: usuário sem permissão para a ação) */
  enabled?: boolean;
}

/**
 * Consulta a política do processo para uma ação protegida do registro aberto.
 * Falha de rede/permissão nunca bloqueia a interface (retorna liberado).
 */
export function useWorkflowGuardCheck({
  actionCode,
  rootEntityType,
  rootEntityId,
  enabled = true,
}: Params): WorkflowGuardDecision {
  const queryClient = useQueryClient();
  const ativo = Boolean(enabled && actionCode && rootEntityType && rootEntityId);

  const chave = ['workflow-guard-check', actionCode, rootEntityType, rootEntityId];

  const { data, isLoading } = useQuery({
    queryKey: chave,
    enabled: ativo,
    // Janela curta: a pendência pode ser resolvida a qualquer momento pelo aprovador
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data: resposta, error } = await supabase.rpc('workflow_guard_check', {
        p_action_code: actionCode,
        p_root_entity_type: rootEntityType as string,
        p_root_entity_id: rootEntityId as string,
      });

      if (error) {
        // Nunca travar a tela por falha na consulta antecipada
        logger.warn('WorkflowGuardCheck', 'Falha ao consultar a trava do processo', {
          actionCode,
          error: error.message,
        });
        return PADRAO_LIBERADO;
      }

      const bruto = (resposta ?? {}) as Record<string, unknown>;
      const resultado = String(bruto.resultado ?? 'ALLOW').toUpperCase();
      const politica: WorkflowGuardDecision['resultado'] =
        resultado === 'DENY' || resultado === 'WARN' ? resultado : 'ALLOW';

      return {
        resultado: politica,
        mensagem: typeof bruto.mensagem === 'string' && bruto.mensagem.trim()
          ? bruto.mensagem.trim()
          : null,
        bloqueado: politica === 'DENY',
        avisa: politica === 'WARN',
      };
    },
  });

  /*
   * Reaproveita o sinal que o painel do processo já usa: quando os produtos do
   * negócio mudam, a instância pode ser aberta/encerrada pelo motor. Assim a
   * decisão em tela acompanha a mudança sem polling permanente.
   */
  useEffect(() => {
    if (!ativo) return;

    const aoMudar = () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-guard-check'] });
    };

    window.addEventListener('workflow:deal-changed', aoMudar);
    return () => window.removeEventListener('workflow:deal-changed', aoMudar);
  }, [ativo, queryClient]);

  return {
    ...(data ?? PADRAO_LIBERADO),
    carregando: ativo && isLoading,
  };
}
